/* eslint-disable no-restricted-imports */
import { useCallback, useEffect, useMemo } from 'react';
import { debounce, trim } from 'lodash';
import { useEffectOnce } from 'react-use';
import { useSwitchDialog } from '../../../routing/ModalRoute';
import { ReportDialog } from '../../../routing/post/ReportDialog/ReportDialog';
import { usePostCollection } from '../../../routing/post/usePostCollection';
import { CreateCollectionFromPostsDialog } from '../../../routing/users/CreateCollectionFromPostsDialog';
import { useCollectionsController } from '../../../routing/users/useCollectionsController';
import { useVideoPlayer } from '../../../utils/VideoPlayerProvider';
import { getIsAuthenticated } from '../../../utils/getIsAuthenticated';
import { useBackButton } from '../../../utils/useBackButton';
import { useTranslation } from '../../../utils/useTranslation';
import { confirmAlert } from '../../feedback/Alert';
import { SmartDrawer } from '../../feedback/SmartDrawer';
import { Flex } from '../../layout/Flex';
import { Typography } from '../../typography/Typography';
import { PostCollectionPreview } from '../PostCollectionPreview/PostCollectionPreview';
import { ActionButton } from '../styles';
import { PER_PAGE, TIMEOUT } from './constants';
import { useCreateCollectionNavigation } from './hooks/useCreateCollectionNavigation';
import { useCreateCollectionSessionStorage } from './hooks/useCreateCollectionSessionStorage';
import { CreateCollectionDialogPage } from './parts/CreateCollectionDialogPage/CreateCollectionDialogPage';
import { Steps } from './typings';

export const CreateCollectionDialog = () => {
    useCollectionsController();
    const t = useTranslation();
    const player = useVideoPlayer();
    const onNavigateBack = useBackButton();
    const switchDialog = useSwitchDialog();

    const { selectedPosts, setSelectedPosts, clearValue, queryString, setQueryString } =
        useCreateCollectionSessionStorage();

    const {
        step,
        previewPost,
        reportData,
        goToPreview,
        openReport,
        openCreateCollection,
        navigateBack,
        handleReportSuccess,
    } = useCreateCollectionNavigation({ setSelectedPosts });

    const isLogged = getIsAuthenticated();

    const postCollection = usePostCollection({
        namespace: 'search-create-collection',
        filters: {
            amount: PER_PAGE,
            query: queryString,
        },
    });

    const onPostSearch = useMemo(
        () =>
            debounce(
                (queryText: string = '') =>
                    postCollection.onUpdateQuery({
                        amount: PER_PAGE,
                        query: queryText,
                    }),
                TIMEOUT,
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [postCollection.onUpdateQuery],
    );

    useEffect(() => {
        if (
            trim(postCollection.filters?.query) !== trim(queryString) &&
            !postCollection.isLoading
        ) {
            onPostSearch(queryString);
        }
    }, [
        queryString,
        postCollection,
        postCollection.onUpdateQuery,
        postCollection.isLoading,
        onPostSearch,
    ]);

    const onInputChange = useCallback(
        (value: string) => {
            setQueryString(value);
        },
        [setQueryString],
    );

    const onClose = useCallback(() => {
        if (selectedPosts.length) {
            confirmAlert(
                'Вы уверены, что хотите закрыть окно?',
                'Все несохраненные изменения будут потеряны.',
            )
                .then(() => onNavigateBack())
                .catch(() => {});
        } else {
            onNavigateBack();
        }
    }, [onNavigateBack, selectedPosts.length]);

    const onNextClick = useCallback(() => {
        postCollection.onReset();
        clearValue();
        postCollection.onLoadNext();
        openCreateCollection(selectedPosts);
    }, [postCollection, clearValue, openCreateCollection, selectedPosts]);

    const onSelectPost = useCallback(
        (postId: string) => {
            const isSelected = selectedPosts.includes(postId);

            setSelectedPosts((prev: string[]) =>
                isSelected ? prev.filter((id) => id !== postId) : [...prev, postId],
            );
        },
        [setSelectedPosts, selectedPosts],
    );

    const onPostCardClick = useCallback(
        ({ postNumber }: { postNumber: number }) => {
            const post = postCollection.items[postNumber];
            if (post && post.id) {
                goToPreview({ ...post, id: post.id });
            }
        },
        [postCollection.items, goToPreview],
    );

    const onPreviewSubmit = useCallback(() => {
        if (previewPost?.id) {
            onSelectPost(previewPost.id);
        }
        navigateBack();
    }, [onSelectPost, previewPost?.id, navigateBack]);

    useEffectOnce(() => {
        if (player) {
            const node = player.getActiveNode();
            node && player.pause(node);
            player.setMuted(true);
        }

        const handleBeforeUnload = () => {
            postCollection.onReset();
            clearValue();
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    });

    const submitButtonText = selectedPosts.includes(previewPost?.id ?? '')
        ? t('Убрать из коллекции')
        : t('Добавить в коллекцию');

    const onAuth = useCallback(() => {
        window.location.href = `${process.env.REACT_APP_PASSPORT_URL}/auth?retpath=${window.location.href}`;
    }, []);

    if (!isLogged) {
        return (
            <SmartDrawer
                titleContent={
                    <Typography mb={{ xs: 1 }} variant="title2" weight="bold">
                        {t('Создание коллекции')}
                    </Typography>
                }
                onClose={onClose}
                mainContent={
                    <Flex direction="column" width="100%">
                        <Typography variant="body" mb={3}>
                            {t(
                                'Чтобы создать коллекцию, войдите с помощью Яндекс ID или зарегистрируйтесь',
                            )}
                        </Typography>
                        <ActionButton onClick={onAuth}>{t('Войти')}</ActionButton>
                    </Flex>
                }
            />
        );
    }

    return (
        <>
            <CreateCollectionDialogPage
                postCollection={postCollection}
                selectedPosts={selectedPosts}
                queryString={queryString}
                step={step}
                onSelectPost={onSelectPost}
                onInputChange={onInputChange}
                onPostCardClick={onPostCardClick}
                onNextClick={onNextClick}
                onClose={onClose}
                onReport={openReport}
            />
            {previewPost && step === Steps.PREVIEW ? (
                <PostCollectionPreview
                    post={previewPost as any}
                    submitButtonText={submitButtonText}
                    onClose={navigateBack}
                    onSubmit={onPreviewSubmit}
                />
            ) : null}
            {step === Steps.REPORT ? (
                <ReportDialog
                    targetId={reportData.targetId}
                    targetType={reportData.targetType}
                    onClose={navigateBack}
                    onReportSuccess={handleReportSuccess}
                />
            ) : null}
            {step === Steps.CREATE_COLLECTION && <CreateCollectionFromPostsDialog />}
        </>
    );
};
